import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { stopImpersonation, IMPERSONATION_END_REASONS } from '../store/admin';
import { decodeJwtExpiration } from '../helpers/jwt';
import { AlertTriangle, X, User, Clock } from 'lucide-react';

/**
 * ImpersonationBanner Component
 *
 * Displays a prominent banner when an admin is impersonating a user.
 * Shows the impersonated user's name, session expiration countdown, and provides an exit button.
 *
 * Features:
 * - Live countdown timer showing time remaining
 * - Auto-logout when session expires
 * - Visual warning when time is running low
 *
 * This banner appears at the top of all pages during impersonation
 * to ensure the admin is always aware they're in impersonation mode.
 */
const ImpersonationBanner = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const isImpersonating = useSelector((st) => st.admin.isImpersonating);
	const impersonatedUser = useSelector((st) => st.admin.impersonatedUser);
	const currentAdmin = useSelector((st) => st.admin.currentAdmin);
	const impersonationInfo = useSelector((st) => st.admin.impersonationInfo);
	const impersonatedToken = useSelector((st) => st.admin.impersonatedToken);

	const [timeRemaining, setTimeRemaining] = useState(null);
	const [isExpiringSoon, setIsExpiringSoon] = useState(false);

	// Calculate expiration time from JWT token
	useEffect(() => {
		if (!impersonatedToken) return;

		const expirationTime = decodeJwtExpiration(impersonatedToken);
		if (expirationTime === null) return;

		// Update countdown every second
		const interval = setInterval(async () => {
			const now = Date.now();
			const remaining = expirationTime - now;

			if (remaining <= 0) {
				// Session expired - await dispatch before navigating
				clearInterval(interval);
				await dispatch(stopImpersonation(IMPERSONATION_END_REASONS.EXPIRED));
				history.push('/admin/clients');
			} else {
				setTimeRemaining(remaining);
				// Warn when less than 5 minutes remaining
				setIsExpiringSoon(remaining < 5 * 60 * 1000);
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [impersonatedToken, dispatch, history]);

	// Helper to format time remaining
	const formatTimeRemaining = (ms) => {
		if (!ms) return '';

		const totalSeconds = Math.floor(ms / 1000);
		const hours = Math.floor(totalSeconds / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = totalSeconds % 60;

		if (hours > 0) {
			return `${hours}h ${minutes}m`;
		} else if (minutes > 0) {
			return `${minutes}m ${seconds}s`;
		} else {
			return `${seconds}s`;
		}
	};

	if (!isImpersonating || !impersonatedUser) {
		return null;
	}

	const handleExitImpersonation = async () => {
		await dispatch(stopImpersonation(IMPERSONATION_END_REASONS.MANUAL));
		// Navigate back to admin clients page
		history.push('/admin/clients');
	};

	const userName = impersonatedUser.firstName && impersonatedUser.lastName
		? `${impersonatedUser.firstName} ${impersonatedUser.lastName}`
		: impersonatedUser.username;

	const adminName = currentAdmin?.firstName && currentAdmin?.lastName
		? `${currentAdmin.firstName} ${currentAdmin.lastName}`
		: currentAdmin?.username || 'Admin';

	return (
		<div
			className={`fixed top-0 left-0 right-0 z-50 shadow-lg border-b-4 transition-colors ${
				isExpiringSoon
					? 'bg-red-500 border-red-700 text-white'
					: 'bg-yellow-500 border-yellow-600 text-black'
			}`}
			role="alert"
			aria-live="assertive"
		>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="py-3 flex items-center justify-between">
					{/* Left side - Warning icon and message */}
					<div className="flex items-center space-x-3 flex-1 min-w-0">
						<AlertTriangle className="h-6 w-6 flex-shrink-0 animate-pulse" aria-hidden="true" />
						<div className="flex flex-col sm:flex-row sm:items-center sm:gap-x-3 flex-1 min-w-0">
							<div className="flex items-center gap-x-2">
								<span className="font-bold text-base sm:text-lg whitespace-nowrap">
									Impersonation Mode
								</span>
								{timeRemaining && (
									<span className={`flex items-center gap-x-1 text-xs sm:text-sm font-semibold px-2 py-1 rounded ${
										isExpiringSoon
											? 'bg-white text-red-600 animate-pulse'
											: 'bg-black bg-opacity-20'
									}`}>
										<Clock className="h-3 w-3 sm:h-4 sm:w-4" aria-hidden="true" />
										{formatTimeRemaining(timeRemaining)}
									</span>
								)}
							</div>
							<span className="text-sm sm:text-base truncate">
								Viewing as <User className="inline h-4 w-4 mx-1" aria-hidden="true" />
								<span className="font-semibold">{userName}</span>
							</span>
						</div>
					</div>

					{/* Right side - Exit button */}
					<button
						onClick={handleExitImpersonation}
						className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors font-medium text-sm sm:text-base shadow-md ml-3 flex-shrink-0 ${
							isExpiringSoon
								? 'bg-white text-red-600 hover:bg-gray-100'
								: 'bg-black text-white hover:bg-gray-800'
						}`}
						aria-label={`Exit impersonation and return to admin dashboard as ${adminName}`}
					>
						<X className="h-5 w-5" aria-hidden="true" />
						<span className="hidden sm:inline">Exit Impersonation</span>
						<span className="sm:hidden">Exit</span>
					</button>
				</div>

				{/* Additional info - only show on larger screens */}
				{impersonationInfo && (
					<div className={`hidden md:block pb-2 text-xs ${
						isExpiringSoon ? 'text-white opacity-90' : 'text-black opacity-75'
					}`}>
						Admin: {adminName} • Started: {new Date(impersonationInfo.startedAt).toLocaleTimeString()}
						{timeRemaining && isExpiringSoon && (
							<span className="ml-2 font-semibold animate-pulse">
								⚠️ Session expiring soon!
							</span>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default ImpersonationBanner;
